/**
 * Word Chains payload, normalised to what the board can play.
 *
 * Mirrors `WordChainPuzzle.parse` in the iOS app: the same
 * `{ category, words, maxMistakes? }` document is served to both, so anything
 * one accepts the other has to accept too.
 */
export interface WordChainLevel {
  words?: unknown;
  category?: unknown;
  maxMistakes?: unknown;
  difficulty?: unknown;
}

export interface NormalizedWordChain {
  /** The chain in order. Word 0 is given; the rest are guessed. */
  words: string[];
  category: string;
  maxMistakes: number;
  difficulty?: string;
}

export const DEFAULT_MAX_MISTAKES = 5;

export function normalize(level: WordChainLevel): NormalizedWordChain | null {
  if (!Array.isArray(level?.words)) return null;
  const words = level.words
    .map((w) => (typeof w === 'string' ? w.trim().toUpperCase() : ''))
    // Letters only: a chain with a digit or a space in it can't be typed on the
    // letter keyboard, so it is content the board must refuse rather than show.
    .filter((w) => w.length > 0 && /^[A-Z]+$/.test(w));
  if (words.length < 2 || words.length !== level.words.length) return null;

  const rawMax = typeof level.maxMistakes === 'number' ? level.maxMistakes : DEFAULT_MAX_MISTAKES;
  return {
    words,
    category: typeof level.category === 'string' ? level.category.trim() : '',
    maxMistakes: Math.max(1, Math.floor(rawMax)),
    difficulty: typeof level.difficulty === 'string' ? level.difficulty : undefined,
  };
}

/** The two-word phrase formed by word `index` and the one after it. */
export function link(words: string[], index: number): string | null {
  if (index < 0 || index + 1 >= words.length) return null;
  return `${words[index]} ${words[index + 1]}`;
}
